/**
 * #1287 — month → LensePanel item mapping for the Gutschrift-Check
 * year→month sidebar. Mirrors the ``lensesToLenseItems`` precedent in
 * ``pages/farm/farmCategoryConfig.tsx``: a pure exported function the
 * page (and the #1289 feed view) feed hook data into.
 *
 * Contract (consumed by #1289 — keep stable):
 *   monthsToLenseItems(availableMonths, gsCountsByMonth) → LenseItem[]
 *
 * ``availableMonths`` is the hook's ASC-sorted ``YYYY-MM[]``; output is
 * years DESC with months DESC inside (newest first, matching the
 * canvas). ``LenseItem``/``LenseSubItem`` carry the display string in
 * ``name`` — NOT ``label``. Month names are German via ``Intl`` with
 * the hardcoded ``de-DE`` locale (the whole gutschrift-check surface
 * is hardcoded German, no i18next).
 *
 * Year badge: pass the hook's ``gsCountsByYear`` (distinct union of
 * settlement ids across the year's months) as the optional third
 * argument — every real caller gets it for free from the same
 * ``useMatchGroups`` return and SHOULD pass it. Without it the helper
 * sums the year's month counts as a fallback; that only equals the
 * distinct union when no settlement event spans two months. #1389
 * cross-month matching broke the old "a settlement event lands in
 * exactly one ``period_ym`` (bucketed by event date)" invariant, so the
 * union is now the authoritative source — don't rely on the sum fallback
 * when it's available. (#1511 — the season ``Gesamt`` roll-up no longer
 * renders as a sidebar lens; it moved to the dashboard panel's leading
 * "Gesamt" card in ``FilterBar``, which derives the total from the
 * unfiltered group set rather than from a per-year/season GS-count union.)
 */
import type { LenseItem, LenseSubItem } from '@hofsync/design-system';

/**
 * #1393 — sentinel ``?ym=`` value selecting the "Gesamt" season view
 * (all months, chronologically) instead of a single ``YYYY-MM``. It is
 * deliberately NOT shaped like a month so it can NEVER alias a real
 * ``period_ym`` the materialiser buckets a settlement event into: the
 * per-month filter in ``useMatchGroups`` and the season filter-skip
 * stay disjoint, and a deep link to a month is never misread as Gesamt.
 * The bare token also reads cleanly in the telemetry ``view`` descriptor
 * (``gutschrift-check-feed:ym=gesamt``).
 */
export const GESAMT_YM = 'gesamt';

/** True iff ``ym`` selects the Gesamt season view (#1393). */
export function isSeasonMode(ym: string): boolean {
  return ym === GESAMT_YM;
}

// #1511 — ``gesamtLensItem`` removed: the "Gesamt" total no longer
// renders as a sidebar lens entry (#1393's misplacement). It moved to
// the dashboard status-panel's leading card in ``FilterBar`` (total
// delivery-group count + non-overlapping grand total €). ``GESAMT_YM`` /
// ``isSeasonMode`` stay — the whole-season set is still the default feed
// view (``useMatchGroups`` season filter-skip) and the canvas page reads
// the sentinel as "no explicit month".

/** 'YYYY-MM' → German month name, e.g. '2026-05' → 'Mai'. */
export function formatMonthName(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long' });
}

export function monthsToLenseItems(
  availableMonths: string[],
  gsCountsByMonth: Record<string, number>,
  gsCountsByYear?: Record<string, number>,
): LenseItem[] {
  // Lexicographic DESC == chronological newest-first for 'YYYY-MM'.
  const monthsDesc = [...availableMonths].sort().reverse();

  const monthsByYear = new Map<string, string[]>();
  for (const ym of monthsDesc) {
    const year = ym.slice(0, 4);
    const bucket = monthsByYear.get(year) ?? [];
    bucket.push(ym);
    monthsByYear.set(year, bucket);
  }

  const items: LenseItem[] = [];
  // Map preserves insertion order; monthsDesc iteration inserted the
  // years newest-first already.
  monthsByYear.forEach((yms, year) => {
    const children: LenseSubItem[] = yms.map((ym) => ({
      id: ym,
      name: formatMonthName(ym),
      count: gsCountsByMonth[ym] ?? 0,
    }));
    const count =
      gsCountsByYear?.[year] ??
      yms.reduce((acc, ym) => acc + (gsCountsByMonth[ym] ?? 0), 0);
    items.push({ id: year, name: year, count, children });
  });
  return items;
}
